import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  // Verificar se deve mostrar o prompt
  const shouldShowPrompt = user && location.pathname.includes('/dashboard');
  
  // Detectar se é dispositivo móvel
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Só salvar o prompt se estivermos no dashboard, com usuário autenticado E em dispositivo móvel
      if (shouldShowPrompt && isMobileDevice()) {
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [shouldShowPrompt]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA foi instalada');
    } else {
      console.log('Instalação do PWA foi cancelada');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Não mostrar novamente nesta sessão
    setDeferredPrompt(null);
  };

  // Não mostrar se não estiver no dashboard, sem usuário autenticado ou não for dispositivo móvel
  if (!shouldShowPrompt || !isMobileDevice() || !showInstallPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <Download className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">
            Instalar Fox Vendas Dashboard
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Instale o app para acesso rápido ao dashboard e funcione offline
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex space-x-2">
        <button
          onClick={handleInstallClick}
          className="flex-1 bg-primary text-white text-xs py-2 px-3 rounded-md hover:bg-primary/90 transition-colors"
        >
          Instalar
        </button>
        <button
          onClick={handleDismiss}
          className="text-gray-500 text-xs py-2 px-3 hover:text-gray-700 transition-colors"
        >
          Agora não
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;